import React, { useState } from 'react';
import { X } from 'lucide-react';
import styles from '../../_css/Agency.module.css';
import { font } from '../Simulation/constants';

const UpdatePasswordModal = ({ open, admin, onClose, onSubmit }) => {
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const email = admin?.email ?? '';

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleClose = () => {
    setNewPassword('');
    setMessage({ type: '', text: '' });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Email is required.' });
      return;
    }
    if (!newPassword.trim()) {
      setMessage({ type: 'error', text: 'Password is required.' });
      return;
    }
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      await onSubmit({ email: email.trim(), password: newPassword });
      setMessage({ type: 'success', text: 'Password updated successfully.' });
      setTimeout(handleClose, 1500);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update password.';
      setMessage({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  };

  const overlayStyle = {
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  };
  const modalStyle = {
    maxWidth: 520,
    width: '100%',
    background: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: 12,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 32px)',
  };
  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  };
  const titleStyle = { fontSize: 20, fontWeight: 700, color: '#363636', margin: 0, fontFamily: font };
  const closeBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    padding: 0,
    border: 'none',
    background: 'rgba(0, 0, 0, 0.06)',
    borderRadius: 8,
    color: '#6b6b6b',
    cursor: 'pointer',
  };
  const bodyStyle = { padding: '20px 22px', overflow: 'auto', flex: 1, background: '#ffffff' };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 500, color: '#6b6b6b', marginBottom: 6, fontFamily: font };
  const inputWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: 8,
    padding: '10px 14px',
    boxSizing: 'border-box',
  };
  const inputWrapperReadOnlyStyle = {
    ...inputWrapperStyle,
    background: 'rgba(0, 0, 0, 0.02)',
  };
  const inputStyle = {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: 14,
    width: '100%',
    fontFamily: font,
    color: '#363636',
  };
  const inputReadOnlyStyle = { ...inputStyle, color: '#999999', cursor: 'default' };
  const footerStyle = {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid rgba(0, 0, 0, 0.08)',
    background: '#fafafa',
    borderRadius: '0 0 12px 12px',
  };
  const cancelBtnStyle = {
    padding: '10px 18px',
    borderRadius: 10,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    background: '#ffffff',
    color: '#363636',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: font,
  };
  const submitBtnStyle = {
    padding: '10px 20px',
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg, #ff4b4f, #dc2626)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer',
    fontFamily: font,
    boxShadow: '0 4px 16px rgba(255, 75, 79, 0.25)',
    opacity: loading ? 0.7 : 1,
  };
  const messageErrorStyle = { fontSize: 13, color: '#b91c1c', marginTop: 8, fontFamily: font };
  const messageSuccessStyle = { fontSize: 13, color: '#16a34a', marginTop: 8, fontFamily: font };

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="update-password-modal-title" style={overlayStyle}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={modalStyle}>
        <div style={headerStyle}>
          <h2 id="update-password-modal-title" style={titleStyle}>Update password</h2>
          <button type="button" style={closeBtnStyle} onClick={handleClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div style={bodyStyle}>
          <form style={{ display: 'flex', flexDirection: 'column', gap: 0 }} onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle} htmlFor="update-password-email">Email</label>
              <div style={inputWrapperReadOnlyStyle}>
                <input
                  id="update-password-email"
                  type="email"
                  style={inputReadOnlyStyle}
                  value={email}
                  readOnly
                />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle} htmlFor="update-password-new">New password</label>
              <div style={inputWrapperStyle}>
                <input
                  id="update-password-new"
                  type="password"
                  style={inputStyle}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
              </div>
            </div>
            {message.text && (
              <p style={message.type === 'error' ? messageErrorStyle : messageSuccessStyle}>{message.text}</p>
            )}
            <div style={footerStyle}>
              <button type="button" style={cancelBtnStyle} onClick={handleClose}>
                Cancel
              </button>
              <button type="submit" style={submitBtnStyle} disabled={loading}>
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePasswordModal
